/**
 * Typed HTTP client for the NestJS API.
 *
 * Default base is the RELATIVE `/api` prefix: in every deployment the ingress
 * routes `/api/*` to the api service and strips the prefix, so the browser
 * talks to the same origin and no API host is baked into the bundle. `VITE_API_URL`
 * is only set for unusual contours — Vite's dev server proxies `/api` to :3001
 * (see vite.config.ts).
 */

import type { AuthMe, HealthResponse } from '@plans/shared';

export type { AuthMe, HealthResponse };

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

/** One field-level issue of a 400/422 answer (`body.issues`, arch §3.3). */
export interface ApiIssue {
  field: string;
  message: string;
}

/**
 * The error envelope of arch §3.3. DTO-shape 400s keep the ValidationPipe
 * format (`message: string[]`, no `code`); every domain error carries the
 * machine-readable `code`, and a 409 additionally carries the CURRENT
 * document so the client can re-sync without a refetch.
 */
export interface ApiErrorBody {
  statusCode?: number;
  code?: string;
  message?: string | string[];
  error?: string;
  issues?: ApiIssue[];
  expectedVersion?: number;
  currentVersion?: number;
  current?: unknown;
}

/** An API error with its HTTP status and the parsed error body. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly method: string,
    readonly path: string,
    message: string,
    readonly body: ApiErrorBody | null = null,
  ) {
    super(`${method} ${path} failed with HTTP ${status}: ${message}`);
    this.name = 'ApiError';
  }

  /** Machine-readable `code` (`invalid`, `not_found`, `conflict`, `invalid_ref`, …). */
  get code(): string | undefined {
    return typeof this.body?.code === 'string' ? this.body.code : undefined;
  }

  /** `true` for a version conflict — the cache can be healed from `currentDocument`. */
  get isConflict(): boolean {
    return this.status === 409 && this.body?.current !== undefined;
  }

  /** Field-level issues of a 400/422, ready for per-field rendering in a form. */
  get issues(): ApiIssue[] {
    const issues = this.body?.issues;
    if (!Array.isArray(issues)) return [];
    return issues.filter(
      (issue): issue is ApiIssue =>
        !!issue && typeof issue.field === 'string' && typeof issue.message === 'string',
    );
  }

  /** `message` of one field's first issue, when the API sent field-level detail. */
  issueFor(field: string): string | undefined {
    return this.issues.find((issue) => issue.field === field)?.message;
  }

  /** The fresh RegistryDocument a 409 carries (`body.current`) — put it into the cache. */
  get currentDocument(): unknown {
    return this.isConflict ? this.body?.current : undefined;
  }
}

/** Anything serialisable into a query string; `undefined`/`null`/'' drop the key. */
export type QueryValue = string | number | boolean | null | undefined;

export function buildQuery(params: object = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, QueryValue>)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

/**
 * Reads the error body once: the human-readable `message` (ValidationPipe
 * arrays are joined) plus the whole envelope for `ApiError`'s accessors.
 */
async function parseError(response: Response): Promise<{ message: string; body: ApiErrorBody | null }> {
  try {
    const body = (await response.json()) as ApiErrorBody;
    if (Array.isArray(body.message)) return { message: body.message.join('; '), body };
    if (typeof body.message === 'string') return { message: body.message, body };
    return { message: JSON.stringify(body), body };
  } catch {
    return { message: response.statusText || 'unknown error', body: null };
  }
}

/**
 * A 401 means the session is gone. The browser is sent to the OAuth entry
 * point with a FULL page load — the session cookie is set by the api on a
 * redirect chain that starts at oauth.yandex.ru, and a client-side route change
 * would leave the already-mounted queries holding a stale 401.
 *
 * `/auth/me` is excluded: it is the probe that *decides* whether we are logged
 * in, so it must be allowed to answer "no" without bouncing the app.
 */
function handleUnauthorized(path: string): void {
  if (path.startsWith('/auth')) return;
  window.location.assign(`${window.location.origin}${API_BASE_URL}/auth/login`);
}

/**
 * The single transport every domain client composes (Wave 6's planning client
 * included). Exported since Wave 6 — before that only the built-in helpers
 * (`health`, `auth`) used it.
 */
export async function request<T>(method: string, path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { Accept: 'application/json', ...(init.headers ?? {}) },
  });
  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(path);
    const { message, body } = await parseError(response);
    throw new ApiError(response.status, method, path, message, body);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Serialisable body helper for the mutation clients that the domain views
 * (Wave 5) add on top of `request`.
 */
export function jsonBody(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

export const health = {
  /** GET /healthz */
  get: (signal?: AbortSignal) => request<HealthResponse>('GET', '/healthz', { signal }),
};

/**
 * Session identity. A 401 here is meaningful — the caller decides what to do
 * (the layout shows the login state, it does not redirect).
 */
export const auth = {
  me: (signal?: AbortSignal) => request<AuthMe>('GET', '/auth/me', { signal }),
  /**
   * Full-page navigation: the api destroys the session, then redirects to the
   * `/logged-out` interstitial, never to /auth/login (that would bounce the
   * user straight back in — see the logout handler in apps/api). `<a href>`
   * would work too; assign() keeps it one line.
   */
  logout: () => {
    window.location.assign(`${API_BASE_URL}/auth/logout`);
  },
  /** Where the "Sign in" button on the interstitial goes. */
  loginUrl: `${API_BASE_URL}/auth/login`,
};

/*
 * Domain clients (registry / projects / streams / teams / people / audit) are
 * added here by Wave 5 — see LLM_CONTEXT/02-architecture.md §3 for the routes.
 */
